import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRebuildAPI } from "@rebuild/shared/api";
import type { EstimateChatMessage } from "@rebuild/shared/models";
import { AgentProposalStatus } from "@rebuild/shared/models";
import { ESTIMATE_CHAT_KEYS } from "@/hooks/queries/estimate-chat";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { syncEstimateDetailFromServerToRedux } from "@/hooks/estimate/syncEstimateDetailFromServer";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { store } from "@/redux/store";
import { applyProposalOutcomes } from "./proposalCache.utils";
import { applyAcceptedProposalsToEstimationDetail } from "./estimationDetail.utils";

const { estimateChatControllerAcceptProposals } = getRebuildAPI();

export function useAcceptProposalsMutation(estimateId: string, chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (proposalIds: string[]) => estimateChatControllerAcceptProposals(estimateId, chatId, { proposalIds }),
    onSuccess: ({ outcomes }) => {
      // The server already wrote the changes. Patch them into the loaded estimate so the room view
      // updates now, then refresh both estimate stores in the background to pick up anything the
      // patch doesn't mirror exactly (recomputed quantities, server-side archive rules).
      const messages = queryClient.getQueryData<EstimateChatMessage[]>(ESTIMATE_CHAT_KEYS.messages(estimateId, chatId)) ?? [];
      const appliedIds = new Set(outcomes.filter((o) => o.status === AgentProposalStatus.applied).map((o) => o.proposalId));
      const applied = messages.flatMap((m) => m.proposals ?? []).filter((p) => appliedIds.has(p.id));
      const estimationDetail = store.getState().estimation.estimationDetail;
      if (estimationDetail && applied.length > 0) {
        const next = applyAcceptedProposalsToEstimationDetail(estimationDetail, applied);
        if (next !== estimationDetail) store.dispatch(estimationActions.setEstimationState({ estimationDetail: next }));
      }

      applyProposalOutcomes(queryClient, estimateId, chatId, outcomes);

      if (applied.length > 0) {
        queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
        void syncEstimateDetailFromServerToRedux(store.dispatch, estimateId, { silent: true });
      }
    },
    onError: (error) => {
      console.error("Accept proposals error:", error);
    },
  });
}
